import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'reactstrap'
import { MaterialReactTable } from 'material-react-table'
import { getRegs, selectValues, update } from '../../../../utils/fc'
import { notifyError } from '../../../../utils/notify'

function diaIso(valor) {
  if (!valor) return ''
  const texto = String(valor).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto) || texto.startsWith('0000')) return ''
  return texto
}

function somarDias(iso, dias) {
  if (!iso || dias === null || dias === undefined || dias === '') return null
  const quantidade = Number(dias)
  if (!Number.isInteger(quantidade) || quantidade < 0) return null
  const data = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(data.getTime())) return null
  data.setDate(data.getDate() + quantidade)
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${data.getFullYear()}-${mes}-${dia}`
}

async function carregarAtividades(projId) {
  const id = Number(projId) || 0
  if (!id) return []

  const atividades = await getRegs('atividade', { proj_id: id }, {
    '(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = atividade.pf_id LIMIT 1)': 'pf_nomeExibicao',
  })
  if (!Array.isArray(atividades)) throw new Error('Falha ao ler atividades')
  return atividades
}

export default function AtividadesProjeto({ project, user }) {
  const [linhas, setLinhas] = useState([])
  const [pessoas, setPessoas] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState(false)

  const recarregar = useCallback(async () => {
    setCarregando(true)
    setErro(false)
    try {
      const [atividades, listaPessoas] = await Promise.all([
        carregarAtividades(project?.proj_id),
        selectValues('pf', 'pf_id', 'pf_nomeExibicao', { pf_ativo: 1 }),
      ])
      setLinhas(atividades)
      setPessoas(Array.isArray(listaPessoas) ? listaPessoas : [])
    } catch (e) {
      setErro(true)
      setLinhas([])
      notifyError('Não foi possível carregar as atividades do projeto.')
    } finally {
      setCarregando(false)
    }
  }, [project?.proj_id])

  useEffect(() => {
    recarregar()
  }, [recarregar])

  const gravar = useCallback(async (atividade, campos) => {
    const { pf_nomeExibicao, ...persistir } = campos
    if ('atividade_dtIniPlanejada' in persistir || 'atividade_prazoDiasExecucao' in persistir) {
      const inicio = 'atividade_dtIniPlanejada' in persistir
        ? persistir.atividade_dtIniPlanejada
        : diaIso(atividade.atividade_dtIniPlanejada)
      const prazo = 'atividade_prazoDiasExecucao' in persistir
        ? persistir.atividade_prazoDiasExecucao
        : atividade.atividade_prazoDiasExecucao
      persistir.atividade_dtFimPlanejada = somarDias(inicio, prazo)
    }
    const id = await update('atividade', atividade.atividade_id, persistir)
    if (!id) {
      notifyError('Não foi possível salvar a atividade.')
      return
    }
    setLinhas(atuais => atuais.map((linha) => (
      Number(linha.atividade_id) === Number(atividade.atividade_id)
        ? { ...linha, ...persistir, ...(pf_nomeExibicao !== undefined ? { pf_nomeExibicao } : {}) }
        : linha
    )))
  }, [])

  const colunas = useMemo(() => ([
    {
      accessorKey: 'atividade_nome',
      header: 'Nome da atividade',
      Cell: ({ row }) => (
        <input
          className="form-control form-control-sm"
          defaultValue={row.original.atividade_nome || ''}
          key={`nome-${row.original.atividade_id}-${row.original.atividade_nome || ''}`}
          onBlur={(event) => {
            const nome = event.target.value
            if (nome === (row.original.atividade_nome || '')) return
            gravar(row.original, { atividade_nome: nome })
          }}
        />
      ),
    },
    {
      accessorKey: 'atividade_dtIniPlanejada',
      header: 'Data de início previsto',
      Cell: ({ row }) => (
        <input
          type="date"
          className="form-control form-control-sm"
          defaultValue={diaIso(row.original.atividade_dtIniPlanejada)}
          key={`ini-${row.original.atividade_id}-${diaIso(row.original.atividade_dtIniPlanejada)}`}
          onBlur={(event) => {
            const inicio = event.target.value || null
            if (inicio === (diaIso(row.original.atividade_dtIniPlanejada) || null)) return
            gravar(row.original, { atividade_dtIniPlanejada: inicio })
          }}
        />
      ),
    },
    {
      accessorKey: 'atividade_prazoDiasExecucao',
      header: 'Dias para executar',
      Cell: ({ row }) => (
        <input
          type="number"
          min="0"
          step="1"
          className="form-control form-control-sm"
          defaultValue={row.original.atividade_prazoDiasExecucao ?? ''}
          key={`dias-${row.original.atividade_id}-${row.original.atividade_prazoDiasExecucao ?? ''}`}
          onBlur={(event) => {
            const texto = event.target.value
            const prazo = texto === '' ? null : Number(texto)
            const atual = row.original.atividade_prazoDiasExecucao == null
              ? null
              : Number(row.original.atividade_prazoDiasExecucao)
            if (prazo === atual) return
            if (prazo != null && (!Number.isInteger(prazo) || prazo < 0)) {
              notifyError('Os dias para executar precisam ser um número inteiro a partir de zero.')
              event.target.value = atual ?? ''
              return
            }
            gravar(row.original, { atividade_prazoDiasExecucao: prazo })
          }}
        />
      ),
    },
    {
      accessorKey: 'atividade_dtFimPlanejada',
      header: 'Data de fim previsto',
      Cell: ({ row }) => (
        <input
          type="date"
          className="form-control form-control-sm"
          defaultValue={diaIso(row.original.atividade_dtFimPlanejada)}
          key={`fim-${row.original.atividade_id}-${diaIso(row.original.atividade_dtFimPlanejada)}`}
          onBlur={(event) => {
            const fim = event.target.value || null
            if (fim === (diaIso(row.original.atividade_dtFimPlanejada) || null)) return
            gravar(row.original, { atividade_dtFimPlanejada: fim })
          }}
        />
      ),
    },
    {
      id: 'responsavel',
      header: 'Responsável',
      accessorFn: row => row.pf_nomeExibicao || '',
      Cell: ({ row }) => (
        <select
          className="form-control form-control-sm"
          value={Number(row.original.pf_id) || ''}
          onChange={(event) => {
            const pfId = event.target.value ? Number(event.target.value) : null
            const pessoa = pessoas.find(item => Number(item.value) === pfId)
            gravar(row.original, {
              pf_id: pfId,
              pf_nomeExibicao: pessoa?.label || '',
            })
          }}
        >
          <option value="">Sem responsável</option>
          {pessoas.map(pessoa => (
            <option key={pessoa.value} value={pessoa.value}>{pessoa.label}</option>
          ))}
        </select>
      ),
    },
  ]), [gravar, pessoas])

  if (!user) {
    return <p>Você não tem permissão para acessar essa tela</p>
  }

  if (carregando) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner />
      </div>
    )
  }

  if (erro) return null

  if (!linhas.length) {
    return <p className="mb-0">Não há atividades.</p>
  }

  return (
    <MaterialReactTable
      columns={colunas}
      data={linhas}
      enableColumnFilters={false}
      enableDensityToggle={false}
      enableGlobalFilter={false}
      enablePagination={linhas.length > 50}
      enableTopToolbar={false}
      initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 50 } }}
      muiTableBodyCellProps={{ sx: { fontSize: '13px' } }}
      muiTableHeadCellProps={{ sx: { fontSize: '12px' } }}
    />
  )
}
